/**
 * AuctionSection - Displays auction info on product page
 * Shows countdown timer, current bid, bid history, and Buy Now button
 */
import { useState, useEffect, useCallback } from "react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  Timer, Gavel, Zap, AlertTriangle, 
  CreditCard, ChevronDown, ChevronUp, Clock, Users, Shield
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AuctionSection({ product, onBidPlaced }) {
  const { isAuthenticated, token } = useAuth();
  const navigate = useNavigate();
  
  const [auction, setAuction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bidAmount, setBidAmount] = useState("");
  const [bidding, setBidding] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [showBidHistory, setShowBidHistory] = useState(false);
  const [bidHistory, setBidHistory] = useState([]);
  const [bidderStatus, setBidderStatus] = useState({ can_bid: true, strikes: 0 });
  const [hasPaymentMethod, setHasPaymentMethod] = useState(false);

  // Fetch auction data
  const fetchAuction = useCallback(async () => {
    if (!product?.auction_id && !product?.id) return;
    
    try {
      const auctionId = product.auction_id;
      if (auctionId) {
        const response = await fetch(`${API_URL}/api/auctions/by-id/${auctionId}`);
        if (response.ok) {
          const data = await response.json();
          setAuction(data);
          
          // Set initial bid amount
          const minBid = data.current_bid_jmd 
            ? data.current_bid_jmd + (data.min_increment_jmd || 100)
            : data.min_bid_jmd;
          setBidAmount(minBid.toString());
        }
      }
    } catch (error) {
      console.error("Error fetching auction:", error);
    }
    setLoading(false);
  }, [product]);

  // Check bidder status and payment method
  const checkBidderStatus = useCallback(async () => {
    if (!isAuthenticated || !token) return;
    
    try {
      // Check bidder status
      const statusRes = await api.get("/auctions/my/status");
      setBidderStatus(statusRes.data);
      
      // Check payment methods
      const pmRes = await api.get("/billing/payment-methods");
      setHasPaymentMethod(pmRes.data?.payment_methods?.length > 0);
    } catch (error) {
      console.error("Error checking bidder status:", error);
    }
  }, [isAuthenticated, token]);

  // Fetch bid history
  const fetchBidHistory = async () => {
    if (!auction?.id) return;
    
    try {
      const response = await fetch(`${API_URL}/api/auctions/${auction.id}/bids`);
      if (response.ok) {
        const data = await response.json();
        setBidHistory(data.bids || []);
      }
    } catch (error) {
      console.error("Error fetching bids:", error);
    }
  };

  useEffect(() => {
    fetchAuction();
    checkBidderStatus();
  }, [fetchAuction, checkBidderStatus]);

  useEffect(() => {
    if (showBidHistory && auction) {
      fetchBidHistory();
    }
  }, [showBidHistory, auction]);

  // Countdown timer
  useEffect(() => {
    if (!auction || auction.status !== "live") return;
    
    const updateTimer = () => {
      const endTime = new Date(auction.end_at).getTime();
      const now = Date.now();
      const diff = endTime - now;
      
      if (diff <= 0) {
        setTimeRemaining(null);
        fetchAuction(); // Refresh to get ended status
        return;
      }
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setTimeRemaining({ days, hours, minutes, seconds, total: diff });
    };
    
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [auction, fetchAuction]);

  // Place bid
  const handlePlaceBid = async () => {
    if (!isAuthenticated) {
      navigate("/login?redirect=" + encodeURIComponent(window.location.pathname));
      return;
    }
    
    if (!hasPaymentMethod) {
      toast.error("Please add a payment method before bidding");
      navigate("/settings?tab=payment-methods&redirect=" + encodeURIComponent(window.location.pathname));
      return;
    }
    
    if (!bidderStatus.can_bid) {
      toast.error(bidderStatus.ban_reason || "You are not allowed to bid");
      return;
    }
    
    const amount = parseFloat(bidAmount);
    const minRequired = auction.current_bid_jmd 
      ? auction.current_bid_jmd + (auction.min_increment_jmd || 100)
      : auction.min_bid_jmd;
    
    if (amount < minRequired) {
      toast.error(`Minimum bid is J$${minRequired.toLocaleString()}`);
      return;
    }
    
    setBidding(true);
    try {
      await api.post(`/auctions/${auction.id}/bid`, {
        amount_jmd: amount,
        delivery_mode: "ADDRESS"
      });
      
      toast.success(`Bid of J$${amount.toLocaleString()} placed successfully!`);
      fetchAuction();
      if (onBidPlaced) onBidPlaced();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to place bid");
    }
    setBidding(false);
  };

  // Buy Now
  const handleBuyNow = async () => {
    if (!isAuthenticated) {
      navigate("/login?redirect=" + encodeURIComponent(window.location.pathname));
      return;
    }
    
    if (!hasPaymentMethod) {
      toast.error("Please add a payment method");
      navigate("/settings?tab=payment-methods&redirect=" + encodeURIComponent(window.location.pathname));
      return;
    }
    
    setBidding(true);
    try {
      await api.post(`/auctions/${auction.id}/buy-now`, {
        delivery_mode: "ADDRESS"
      });
      
      toast.success("Purchase successful! Proceeding to checkout...");
      navigate("/my-orders");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to complete purchase");
    }
    setBidding(false);
  };

  // Navigate to add payment method
  const handleAddPaymentMethod = () => {
    navigate("/settings?tab=payment-methods&redirect=" + encodeURIComponent(window.location.pathname));
  };

  if (loading) {
    return (
      <Card className="bg-slate-900/50 border-slate-800 animate-pulse">
        <CardContent className="p-6">
          <div className="h-32 bg-slate-800 rounded"></div>
        </CardContent>
      </Card>
    );
  }

  if (!auction || !product.auction_enabled) {
    return null;
  }

  const isLive = auction.status === "live";
  const isEnded = auction.status === "ended";
  const isScheduled = auction.status === "scheduled";

  return (
    <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/5 border-amber-500/30" data-testid="auction-section">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-amber-500">
            <Gavel className="w-5 h-5" />
            Auction
          </CardTitle>
          <Badge className={`${
            isLive ? "bg-green-600 animate-pulse" : 
            isEnded ? "bg-slate-600" : 
            "bg-blue-600"
          }`}>
            {isLive ? "LIVE" : isEnded ? "ENDED" : "UPCOMING"}
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Countdown Timer */}
        {isLive && timeRemaining && (
          <div className="bg-slate-900/50 rounded-lg p-4">
            <p className="text-xs text-slate-400 mb-2 flex items-center gap-1">
              <Timer className="w-3 h-3" /> Time Remaining
            </p>
            <div className="grid grid-cols-4 gap-2 text-center">
              {timeRemaining.days > 0 && (
                <div>
                  <p className="text-2xl font-bold text-amber-500">{timeRemaining.days}</p>
                  <p className="text-xs text-slate-400">Days</p>
                </div>
              )}
              <div>
                <p className="text-2xl font-bold text-amber-500">{String(timeRemaining.hours).padStart(2, "0")}</p>
                <p className="text-xs text-slate-400">Hours</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-500">{String(timeRemaining.minutes).padStart(2, "0")}</p>
                <p className="text-xs text-slate-400">Mins</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-500">{String(timeRemaining.seconds).padStart(2, "0")}</p>
                <p className="text-xs text-slate-400">Secs</p>
              </div>
            </div>
          </div>
        )}

        {/* Scheduled Timer */}
        {isScheduled && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-center">
            <Clock className="w-6 h-6 mx-auto text-blue-400 mb-2" />
            <p className="text-sm text-slate-300">Auction starts</p>
            <p className="text-lg font-semibold text-blue-400">
              {new Date(auction.start_at).toLocaleString()}
            </p>
          </div>
        )}

        {/* Current Bid Info */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-slate-900/50 rounded-lg p-3">
            <p className="text-xs text-slate-400 mb-1">Current Bid</p>
            <p className="text-xl font-bold text-emerald-500" data-testid="current-bid">
              J${auction.current_bid_jmd?.toLocaleString() || auction.min_bid_jmd?.toLocaleString()}
            </p>
            <p className="text-xs text-slate-400">{auction.bid_count || 0} bids</p>
          </div>
          
          {auction.buy_now_enabled && auction.buy_now_price_jmd && (
            <div className="bg-slate-900/50 rounded-lg p-3">
              <p className="text-xs text-slate-400 mb-1 flex items-center gap-1">
                <Zap className="w-3 h-3" /> Buy Now Price
              </p>
              <p className="text-xl font-bold text-amber-500" data-testid="buy-now-price">
                J${auction.buy_now_price_jmd?.toLocaleString()}
              </p>
              <p className="text-xs text-slate-400">Skip bidding</p>
            </div>
          )}
        </div>

        {/* Bidding Controls */}
        {isLive && !isEnded && (
          <>
            {/* Bidder Status Warning */}
            {bidderStatus.strikes > 0 && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                <p className="text-sm text-yellow-400 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  You have {bidderStatus.strikes} strike(s). {bidderStatus.strikes >= 2 ? "One more and you'll be banned!" : ""}
                </p>
              </div>
            )}

            {!bidderStatus.can_bid && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                <p className="text-sm text-red-400 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  {bidderStatus.ban_reason || "You are currently banned from bidding"}
                </p>
              </div>
            )}

            {/* Payment Method Warning */}
            {isAuthenticated && !hasPaymentMethod && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                <p className="text-sm text-amber-400 mb-2 flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Add a payment method to bid
                </p>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="border-amber-500 text-amber-400"
                  onClick={handleAddPaymentMethod}
                  data-testid="add-card-btn"
                >
                  Add Card
                </Button>
              </div>
            )}

            {/* Bid Input */}
            {bidderStatus.can_bid && (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">J$</span>
                    <Input
                      type="number"
                      value={bidAmount}
                      onChange={(e) => setBidAmount(e.target.value)}
                      className="pl-10 bg-slate-900 border-slate-700"
                      placeholder="Enter bid amount"
                      min={auction.current_bid_jmd ? auction.current_bid_jmd + auction.min_increment_jmd : auction.min_bid_jmd}
                      data-testid="bid-amount-input"
                    />
                  </div>
                  <Button
                    onClick={handlePlaceBid}
                    disabled={bidding || !hasPaymentMethod}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="place-bid-btn"
                  >
                    {bidding ? "Bidding..." : "Place Bid"}
                  </Button>
                </div>
                <p className="text-xs text-slate-400">
                  Min bid: J${(auction.current_bid_jmd ? auction.current_bid_jmd + (auction.min_increment_jmd || 100) : auction.min_bid_jmd)?.toLocaleString()}
                  {" "}&bull;{" "}
                  Min increment: J${(auction.min_increment_jmd || 100).toLocaleString()}
                </p>
              </div>
            )}

            {/* Buy Now Button */}
            {auction.buy_now_enabled && auction.buy_now_price_jmd && (
              <Button
                onClick={handleBuyNow}
                disabled={bidding}
                className="w-full bg-amber-600 hover:bg-amber-700"
                size="lg"
                data-testid="buy-now-btn"
              >
                <Zap className="w-4 h-4 mr-2" />
                Buy Now for J${auction.buy_now_price_jmd?.toLocaleString()}
              </Button>
            )}
          </>
        )}

        {/* Ended Message */}
        {isEnded && (
          <div className="bg-slate-800/50 rounded-lg p-4 text-center">
            <p className="text-slate-400">This auction has ended</p>
            {auction.current_bid_jmd && (
              <p className="text-lg font-semibold text-emerald-500 mt-2">
                Winning bid: J${auction.current_bid_jmd?.toLocaleString()}
              </p>
            )}
          </div>
        )}

        {/* Bid History Toggle */}
        <button
          onClick={() => setShowBidHistory(!showBidHistory)}
          className="w-full flex items-center justify-center gap-2 text-sm text-slate-400 hover:text-white py-2"
          data-testid="toggle-bid-history"
        >
          {showBidHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          {showBidHistory ? "Hide" : "Show"} Bid History ({auction.bid_count || 0})
        </button>

        {/* Bid History */}
        {showBidHistory && bidHistory.length > 0 && (
          <div className="bg-slate-900/50 rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
            {bidHistory.map((bid, index) => (
              <div key={bid.id || index} className="flex items-center justify-between text-sm py-1 border-b border-slate-800 last:border-0">
                <div className="flex items-center gap-2">
                  <Users className="w-3 h-3 text-slate-500" />
                  <span className="text-slate-300">{bid.bidder_name || "Bidder"}</span>
                </div>
                <span className="font-semibold text-emerald-400">J${bid.amount_jmd?.toLocaleString()}</span>
              </div>
            ))}
          </div>
        )}

        {/* Trust Info */}
        <div className="flex items-center gap-2 text-xs text-slate-400 pt-2 border-t border-slate-800">
          <Shield className="w-3 h-3" />
          <span>Secure bidding &bull; Winners charged automatically</span>
        </div>
      </CardContent>
    </Card>
  );
}
